/**
 * SubjectIdChips — CRIO subject-ID pill(s) in the ContactDetail identity header.
 *
 * The owner's explicit ask in the clinician-portal P0 scope: a recruiter looking at
 * a lead should be able to see the CRIO subject ID without opening CRIO. The ids
 * come from crm.ctms_subject_ids, promoted out of ctms_sync.metadata so they can be
 * uniquely indexed (master plan §6.5) — the same table the P3 lab inbox will file
 * against.
 *
 * WHICH NUMBER (owner report, 2026-08-06): the pill shows `study_subject_id` —
 * the study's own "Subject ID#" as it appears on the CRIO subject profile — NOT
 * `crio_subject_id`, which is CRIO's internal subject record number and reads to
 * staff as just another CRIO id. The internal id remains the identity key
 * everywhere else (lab filing, webhook reverse-lookup); here it is only a
 * fallback for the window between a subject being created in CRIO and a
 * coordinator assigning its number, and it says so in the tooltip.
 *
 * Follows PushToCtmsButton's probe-and-hide contract exactly: GET
 * /contacts/:id/ctms answers 503 ctms_disabled when CRM_CTMS_ENABLED is off, and
 * this renders NOTHING. It also renders nothing when the lead has no subject id yet,
 * which is the common case (a CRIO patient exists long before becoming a study
 * subject), so the header stays exactly as it was for most leads.
 *
 * TRADEOFF, stated: this issues its own GET rather than sharing PushToCtmsButton's.
 * That is one extra cheap request per contact view, accepted because the two mount
 * in different places (the chips row here vs. the action row / phone bottom sheet,
 * where the button may not be mounted at all) and because keeping the probe local
 * makes this purely additive — no ContactDetail data-flow surgery.
 */
import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import api from '../../services/api';
import { displayId, tooltipFor, type SubjectIdRow } from './subjectIdDisplay';

export default function SubjectIdChips({
  contactId,
  /**
   * When set, the pills are wrapped in a div with this class — used on phones,
   * where the existing chip row only renders if the lead is opted-out/archived/
   * snoozed. Self-wrapping keeps an empty row from taking up vertical space for
   * the majority of leads that have no subject id.
   */
  wrapperClassName,
  /** Phone headers use the denser 11px chip scale. */
  compact = false,
}: {
  contactId: string;
  wrapperClassName?: string;
  compact?: boolean;
}) {
  const [rows, setRows] = useState<SubjectIdRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    api
      .get(`/api/crm/contacts/${contactId}/ctms`)
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data?.subject_ids) ? res.data.subject_ids : [];
        setRows(list);
      })
      .catch(() => {
        // 503 = CTMS off; anything else = transient. Either way the chip simply
        // does not appear: a subject id is informational, never worth an error
        // banner in the identity header.
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [contactId]);

  if (!rows || rows.length === 0) return null;

  // One pill per (study, subject). More than a couple is unusual — it means CRIO
  // holds duplicate subjects for this patient — so show the first few and roll the
  // rest into a count rather than letting the header wrap unboundedly.
  const shown = rows.slice(0, 3);
  const extra = rows.length - shown.length;
  const scale = compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-xs';

  const pills = (
    <>
      {shown.map((r) => (
        <span
          key={`${r.study_id || 'nostudy'}:${r.crio_subject_id}`}
          title={tooltipFor(r)}
          className={`inline-flex items-center gap-1 rounded-full bg-cyan-50 font-medium text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300 ${scale}`}
        >
          <FlaskConical className="h-3 w-3" />
          <span className="font-mono">{displayId(r)}</span>
        </span>
      ))}
      {extra > 0 && (
        <span
          title={rows
            .slice(3)
            .map((r) => `${displayId(r)}${r.study_label ? ` · ${r.study_label}` : ''}`)
            .join('\n')}
          className={`inline-flex items-center rounded-full bg-gray-100 font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300 ${scale}`}
        >
          +{extra} more
        </span>
      )}
    </>
  );

  return wrapperClassName ? <div className={wrapperClassName}>{pills}</div> : pills;
}
